import { RepoTree } from "../../utils/RepoTree";
import useStore from "../../Store/Store";
import { logger } from "../../utils/Logger";
import { isIgnoredPath } from "../../utils/CodeCanvasIgnore";
import {
  startRecordingActions,
  saveRecordedActions,
} from "../../Store/UndoManager";

export interface AutoRepoDiagramOptions {
  maxDepth?: number;
  generateFoldersOnly?: boolean;
}

interface RepoTreeNode {
  name: string;
  path: string;
  type: "tree" | "blob";
  children: Map<string, RepoTreeNode>;
}

/**
 * Check if repository diagram generation is needed
 * Returns true if more than 70% of root-level repo files don't have diagram elements
 */
export function needsRepositoryDiagramGeneration(): boolean {
  const { repoData, currentRepo } = useStore.getState();

  if (!repoData || !currentRepo) {
    return false;
  }

  // Get all root-level files (files without "/" in their path, excluding the repo root itself)
  const rootLevelFiles = Object.entries(repoData).filter(([path, data]) => {
    // Skip the repo root entry
    if (path === currentRepo) {
      return false;
    }

    // Check if it's a root-level file (no "/" in path)
    const isRootLevel = !path.includes("/");

    // Only include files (blobs), not folders (trees), and not ignored paths
    return isRootLevel && data.type === "blob" && !isIgnoredPath(path);
  });

  if (rootLevelFiles.length === 0) {
    logger.info("No root-level files found for diagram generation check");
    return false;
  }

  // Count files without diagram cells
  const filesWithoutDiagrams = rootLevelFiles.filter(
    ([path, data]) => !data.cellId || data.cellId === ""
  ).length;

  const percentage = (filesWithoutDiagrams / rootLevelFiles.length) * 100;

  logger.info(
    `Repository diagram generation check: ${filesWithoutDiagrams}/${
      rootLevelFiles.length
    } root-level files (${percentage.toFixed(
      1
    )}%) are not connected to diagram elements`
  );

  return percentage > 70;
}

/**
 * Automatically generates a repository diagram by traversing the repo tree
 * in breadth-first manner and creating cells for each directory/file
 */
export const generateRepositoryDiagram = async (
  options: AutoRepoDiagramOptions = {}
): Promise<boolean> => {
  const { maxDepth = 3, generateFoldersOnly = true } = options;

  const { repoTree, setAutoRepoDiagramProgress, autoRepoDiagramConfig } =
    useStore.getState();

  if (!repoTree) {
    logger.error("AutoRepoDiagramGenerator: No repo tree available");
    return false;
  }

  logger.info(
    "AutoRepoDiagramGenerator: Starting repository diagram generation",
    {
      maxDepth,
      generateFoldersOnly,
      maxCells: autoRepoDiagramConfig.maxCells,
    }
  );

  try {
    // Calculate total items to process for progress tracking
    const totalItems = calculateTotalItems(
      repoTree.root,
      maxDepth,
      generateFoldersOnly
    );
    const totalLevels = Math.min(maxDepth, calculateMaxDepth(repoTree.root));

    // Initialize progress tracking
    setAutoRepoDiagramProgress({
      total: totalItems,
      current: 0,
      isGenerating: true,
      currentLevel: 0,
      totalLevels: totalLevels,
      currentFolderPath: "",
      currentFolderName: "repository root",
      currentCells: 0,
    });

    await startRecordingActions("ADD_FILE_NODE", {
      supressWarning: true,
    });

    // Generate diagram with proper sequential processing and linking
    const success = await generateDiagramBreadthFirst(
      repoTree.root,
      maxDepth,
      generateFoldersOnly
    );

    if (success) {
      await saveRecordedActions("ADD_FILE_NODE");
      logger.info(
        "AutoRepoDiagramGenerator: Repository diagram generation completed successfully"
      );
    } else {
      logger.warn(
        "AutoRepoDiagramGenerator: Repository diagram generation completed with some failures"
      );
    }

    // Reset progress tracking
    setAutoRepoDiagramProgress({
      isGenerating: false,
      current: totalItems,
      currentFolderName: "Completed",
    });

    return success;
  } catch (error) {
    logger.error(
      "AutoRepoDiagramGenerator: Failed to generate repository diagram",
      error
    );

    // Reset progress tracking on error
    useStore.getState().setAutoRepoDiagramProgress({
      isGenerating: false,
      currentFolderName: "Error occurred",
    });

    return false;
  }
};

/**
 * Helper function to generate diagram using breadth-first traversal
 */
const generateDiagramBreadthFirst = async (
  rootNode: RepoTreeNode,
  maxDepth: number,
  generateFoldersOnly: boolean
): Promise<boolean> => {
  // Queue items: { node, depth, parentCellId, nodePath }
  const queue: Array<{
    node: RepoTreeNode;
    depth: number;
    parentCellId?: string;
    nodePath: string;
  }> = [];

  // Map to store created cell IDs for parent-child relationships
  const nodePathToCellId = new Map<string, string>();
  const {
    setAutoRepoDiagramProgress,
    autoRepoDiagramConfig,
    setErrorNotification,
  } = useStore.getState();
  let totalProcessed = 0;
  let totalSkipped = 0;
  let totalFailed = 0;
  let currentCells = 0;
  let maxCellsReached = false;

  // Initialize queue with root children
  const rootChildren = Array.from(rootNode.children.values()).sort(
    (a: RepoTreeNode, b: RepoTreeNode) => a.name.localeCompare(b.name)
  );

  logger.debug.info(
    `AutoRepoDiagramGenerator: Found ${rootChildren.length} root level items`
  );

  rootChildren.forEach((child) => {
    if (shouldIncludeNode(child, 0, maxDepth, generateFoldersOnly)) {
      queue.push({
        node: child,
        depth: 0,
        parentCellId: undefined,
        nodePath: child.path,
      });
    }
  });

  logger.debug.info(
    `AutoRepoDiagramGenerator: Queued ${queue.length} items for processing`
  );

  // Process level by level
  let currentLevel = 0;
  while (queue.length > 0 && !maxCellsReached) {
    const currentLevelItems: Array<{
      node: RepoTreeNode;
      depth: number;
      parentCellId?: string;
      nodePath: string;
    }> = [];

    // Collect all items at current depth
    const currentDepth = queue[0].depth;
    while (queue.length > 0 && queue[0].depth === currentDepth) {
      currentLevelItems.push(queue.shift()!);
    }

    // Update progress for current level
    setAutoRepoDiagramProgress({
      currentLevel: currentDepth + 1,
      currentFolderName:
        currentLevelItems.length > 0
          ? `Level ${currentDepth + 1} items`
          : "Processing...",
      currentCells: currentCells,
    });

    logger.debug.info(
      `AutoRepoDiagramGenerator: Processing level ${currentDepth} with ${currentLevelItems.length} items`
    );

    // Process each item in the current level SEQUENTIALLY to avoid race conditions
    for (let i = 0; i < currentLevelItems.length && !maxCellsReached; i++) {
      const item = currentLevelItems[i];

      // Check if we're about to exceed the maximum cell limit
      if (currentCells >= autoRepoDiagramConfig.maxCells) {
        maxCellsReached = true;
        const errorMessage = `Auto repository diagram generation stopped: Maximum cell limit of ${autoRepoDiagramConfig.maxCells} reached. Consider increasing the limit or reducing the diagram scope.`;
        setErrorNotification(errorMessage);
        logger.warn(`AutoRepoDiagramGenerator: ${errorMessage}`);
        break;
      }

      // Update progress with current folder being processed
      setAutoRepoDiagramProgress({
        current: totalProcessed + totalSkipped + totalFailed,
        currentFolderPath: item.nodePath,
        currentFolderName: item.node.name,
        currentCells: currentCells,
      });

      try {
        logger.debug.info(
          `AutoRepoDiagramGenerator: Processing item ${i + 1}/${
            currentLevelItems.length
          } at level ${currentDepth}: ${item.nodePath}`
        );

        const cellId = await createCellForNodeWithProperLinking(
          item.node,
          item.parentCellId,
          generateFoldersOnly
        );

        if (cellId) {
          nodePathToCellId.set(item.nodePath, cellId);
          totalProcessed++;
          currentCells++; // Increment cell count

          // Update progress with new cell count
          setAutoRepoDiagramProgress({
            currentCells: currentCells,
          });

          logger.debug.info(
            `AutoRepoDiagramGenerator: Successfully created and linked cell ${cellId} for ${item.nodePath} (${currentCells}/${autoRepoDiagramConfig.maxCells} cells)`
          );

          // Add children to queue for next level if within depth limit and not at cell limit
          if (
            item.depth < maxDepth &&
            currentCells < autoRepoDiagramConfig.maxCells
          ) {
            const children = Array.from(item.node.children.values()).sort(
              (a: RepoTreeNode, b: RepoTreeNode) => a.name.localeCompare(b.name)
            );

            const eligibleChildren = children.filter((child) =>
              shouldIncludeNode(
                child,
                item.depth + 1,
                maxDepth,
                generateFoldersOnly
              )
            );

            if (eligibleChildren.length > 0) {
              logger.debug.info(
                `AutoRepoDiagramGenerator: Adding ${
                  eligibleChildren.length
                } children of ${item.nodePath} to queue for level ${
                  item.depth + 1
                }`
              );

              eligibleChildren.forEach((child) => {
                queue.push({
                  node: child,
                  depth: item.depth + 1,
                  parentCellId: cellId,
                  nodePath: child.path,
                });
              });
            }
          }
        } else {
          totalSkipped++;
          logger.debug.warn(
            `AutoRepoDiagramGenerator: Skipped item ${item.nodePath} (already exists or failed to create)`
          );
        }

        // Add a delay between each cell creation to avoid overwhelming the system
        if (i < currentLevelItems.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      } catch (error) {
        totalFailed++;
        logger.error(
          `AutoRepoDiagramGenerator: Failed to create cell for node ${item.nodePath}:`,
          error
        );
      }
    }

    currentLevel++;
    logger.debug.info(
      `AutoRepoDiagramGenerator: Completed level ${currentDepth}. Remaining in queue: ${queue.length}`
    );

    // Add a longer delay between levels to ensure state consistency
    if (queue.length > 0 && !maxCellsReached) {
      await new Promise((resolve) => setTimeout(resolve, 300));
    }
  }

  if (maxCellsReached) {
    logger.info(
      `AutoRepoDiagramGenerator: Generation stopped due to cell limit. Created: ${currentCells}, Processed: ${totalProcessed}, Skipped: ${totalSkipped}, Failed: ${totalFailed}`
    );
  } else {
    logger.info(
      `AutoRepoDiagramGenerator: Generation complete. Created: ${currentCells}, Processed: ${totalProcessed}, Skipped: ${totalSkipped}, Failed: ${totalFailed}`
    );
  }

  return totalProcessed > 0;
};

/**
 * Determines if a node should be included in the diagram generation
 */
const shouldIncludeNode = (
  node: RepoTreeNode,
  depth: number,
  maxDepth: number,
  generateFoldersOnly: boolean
): boolean => {
  // Check depth limit
  if (depth >= maxDepth) {
    return false;
  }

  // Check if path is ignored
  if (node.path && isIgnoredPath(node.path)) {
    return false;
  }

  // Check if we should skip files
  if (generateFoldersOnly && node.type === "blob") {
    return false;
  }

  return true;
};

/**
 * Calculates the total number of items that will be processed for progress tracking
 */
const calculateTotalItems = (
  rootNode: RepoTreeNode,
  maxDepth: number,
  generateFoldersOnly: boolean
): number => {
  let total = 0;
  const queue: Array<{ node: RepoTreeNode; depth: number }> = [];

  // Add root children to queue
  const rootChildren = Array.from(rootNode.children.values());
  rootChildren.forEach((child) => {
    if (shouldIncludeNode(child, 0, maxDepth, generateFoldersOnly)) {
      queue.push({ node: child, depth: 0 });
    }
  });

  // Count all items that will be processed
  while (queue.length > 0) {
    const { node, depth } = queue.shift()!;
    total++;

    // Add children if within depth limit
    if (depth < maxDepth) {
      const children = Array.from(node.children.values());
      children.forEach((child) => {
        if (
          shouldIncludeNode(child, depth + 1, maxDepth, generateFoldersOnly)
        ) {
          queue.push({ node: child, depth: depth + 1 });
        }
      });
    }
  }

  return total;
};

/**
 * Calculates the maximum depth of the tree structure
 */
const calculateMaxDepth = (rootNode: RepoTreeNode): number => {
  let maxDepth = 0;

  const traverse = (node: RepoTreeNode, currentDepth: number) => {
    maxDepth = Math.max(maxDepth, currentDepth);

    for (const child of node.children.values()) {
      traverse(child, currentDepth + 1);
    }
  };

  traverse(rootNode, 0);
  return maxDepth;
};

/**
 * Creates a cell for a given repo tree node following the exact same pattern as manual cell creation
 * This ensures proper linking via the addCellsHandler mechanism
 */
const createCellForNodeWithProperLinking = async (
  node: RepoTreeNode,
  parentCellId?: string,
  generateFoldersOnly?: boolean
): Promise<string | null> => {
  const { repoData } = useStore.getState();

  const file = repoData[node.path];
  if (!file) {
    logger.debug.warn(
      `AutoRepoDiagramGenerator: No repo data found for path: ${node.path}`
    );
    return null;
  }

  // Skip if already has a cell
  if (file.cellId) {
    logger.debug.info(
      `AutoRepoDiagramGenerator: File already has cell, skipping: ${node.path} -> ${file.cellId}`
    );
    return file.cellId;
  }

  try {
    logger.debug.info(
      `AutoRepoDiagramGenerator: Creating cell for ${node.path} (parent: ${
        parentCellId || "none"
      })`
    );

    // Step 1: Clear any selected cell to ensure clean state
    const {
      setSelectedCell,
      setCurrentPath,
      setIsCellCreationRequestInitiatedFromCodeCanvas,
      postToDrawioWaitForResponse,
    } = useStore.getState();

    setSelectedCell(""); // Clear selection to match manual flow conditions

    // Step 2: Set up the creation request flag (MUST be done before setting current path)
    setIsCellCreationRequestInitiatedFromCodeCanvas(true);

    // Step 3: Set current path to the file we want to link (this triggers the linking logic)
    await setCurrentPath(file.path, true);

    // Small delay to ensure path change is processed
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Step 4: Create the cell using the same logic as manual addCellHandler
    let response: any = null;

    if (parentCellId) {
      // Create as child of specified parent
      logger.debug.info(
        `AutoRepoDiagramGenerator: Creating as child of ${parentCellId}`
      );
      response = await postToDrawioWaitForResponse({
        action: "NEW_LINKED_CELL",
        data: {
          newCellName: file.fileName,
          parentCellId: parentCellId,
        },
      });
    } else {
      // Find closest parent and create as its child, or create top-level
      const closestParent = findClosestParentWithCell(file.path, repoData);

      if (closestParent) {
        logger.debug.info(
          `AutoRepoDiagramGenerator: Creating as child of closest parent ${closestParent.cellId}`
        );
        response = await postToDrawioWaitForResponse({
          action: "NEW_LINKED_CELL",
          data: {
            newCellName: file.fileName,
            parentCellId: closestParent.cellId,
          },
        });
      } else {
        // Create top-level cell
        logger.debug.info(
          `AutoRepoDiagramGenerator: Creating as top-level cell`
        );
        response = await postToDrawioWaitForResponse({
          action: "NEW_LINKED_CELL",
          data: { newCellName: file.fileName },
        });
      }
    }

    // Step 5: Wait for the addCellsHandler to process and link the cell
    // The addCellsHandler will automatically handle the linking if:
    // - isCellCreationRequestInitiatedFromCodeCanvas is true
    // - currentPath is set to the file path
    // - no cell is selected
    await new Promise((resolve) => setTimeout(resolve, 200));

    // Step 6: Verify the cell was created and linked
    const updatedFile = useStore.getState().repoData[node.path];
    const cellId = updatedFile?.cellId;

    if (cellId) {
      logger.debug.info(
        `AutoRepoDiagramGenerator: Successfully created and linked cell: ${node.path} -> ${cellId}`
      );
    } else {
      logger.debug.warn(
        `AutoRepoDiagramGenerator: Cell was created but linking may have failed for: ${node.path}`
      );
    }

    // Step 7: Reset flags to clean state for next operation
    const currentState = useStore.getState();
    if (currentState.pausePathChangeToDrawio) {
      await currentState.setPausePathChangeToDrawio(false);
    }

    // The isCellCreationRequestInitiatedFromCodeCanvas flag should be automatically reset by addCellsHandler
    // but if something went wrong, reset it manually
    if (currentState.isCellCreationRequestInitiatedFromCodeCanvas) {
      logger.debug.warn(
        `AutoRepoDiagramGenerator: Flag was not reset by addCellsHandler, resetting manually for ${node.path}`
      );
      setIsCellCreationRequestInitiatedFromCodeCanvas(false);
    }

    return cellId || null;
  } catch (error) {
    logger.debug.error(
      `AutoRepoDiagramGenerator: Error creating cell for node ${node.path}:`,
      error
    );

    // Clean up flags on error
    const currentState = useStore.getState();
    if (currentState.isCellCreationRequestInitiatedFromCodeCanvas) {
      currentState.setIsCellCreationRequestInitiatedFromCodeCanvas(false);
    }
    if (currentState.pausePathChangeToDrawio) {
      await currentState.setPausePathChangeToDrawio(false);
    }

    return null;
  }
};

/**
 * Helper function to find closest parent with cellId (same as in RepoFile.tsx)
 */
const findClosestParentWithCell = (filePath: string, repoData: any) => {
  const pathParts = filePath.split("/");

  // Start from the immediate parent and go up
  for (let i = pathParts.length - 2; i >= 0; i--) {
    const parentPath = pathParts.slice(0, i + 1).join("/");
    const parentData = repoData[parentPath];

    if (parentData && parentData.cellId) {
      return parentData;
    }
  }

  return null;
};
